/*=============================================== All pages ===============================================*/

import type { PageType } from "data/types"

import { Link } from "react-router-dom"

import { Text, CodeContainer } from "tsx-library-julseb"

export const allPages: PageType[] = [
    // Styles
    {
        title: "Colors",
        include: ["ThemeLight", "ThemeDark"],
        category: "styles",
        intro: (
            <>
                <Text>
                    Here, we are using Styled Components{" "}
                    <a
                        href="https://styled-components.com/docs/advanced"
                        target="_blank"
                        rel="noreferrer noopener"
                    >
                        ThemeProvider
                    </a>
                    . All colors have been set in the <code>:root</code>{" "}
                    selector of
                    <code>index.css</code>. If you want to edit them, see the
                    section <code>Overrides</code>{" "}
                    <Link
                        to={{
                            pathname: "/getting-started",
                            hash: "#overrides",
                        }}
                    >
                        here
                    </Link>
                    . If you do not want to use ThemeProvider (for example if
                    you don't a switch on your project), the default colors are
                    the light colors.
                </Text>

                <Text>
                    For each color name, you can write a shorthand version,{" "}
                    which will return the color with the <code>500</code> value.{" "}
                    For example, typing <code>color="primary"</code> will return{" "}
                    <code>Primary500</code>.
                </Text>
            </>
        ),
    },
    {
        title: "Overlays",
        category: "styles",
    },
    {
        title: "Text",
        category: "styles",
    },
    {
        title: "Shadows",
        category: "styles",
    },
    {
        title: "Radiuses",
        category: "styles",
    },
    {
        title: "Theme Provider",
        category: "styles",
        include: ["ThemeContext", "ThemeProvider"],
        types: ["ThemeContextProps"],
        intro: (
            <Text>
                Here, Styled Components{" "}
                <a
                    href="https://styled-components.com/docs/advanced"
                    target="_blank"
                    rel="noreferrer noopener"
                >
                    ThemeProvider
                </a>{" "}
                and{" "}
                <a
                    href="https://reactjs.org/docs/hooks-reference.html#usecontext"
                    target="_blank"
                    rel="noreferrer noopener"
                >
                    React useContext hook
                </a>
                , to switch between light and dark mode. If you do not want to
                use it, all the default colors will be the light ones.
            </Text>
        ),
    },

    // Layouts
    {
        title: "Container",
        include: ["Wrapper", "Main", "Aside"],
        category: "layouts",
    },
    {
        title: "Section",
        category: "layouts",
        as: "section",
    },
    {
        title: "Spacers",
        include: ["Spacers"],
        category: "layouts",
    },
    {
        title: "Grid",
        category: "layouts",
        as: "div",
    },
    {
        title: "Flexbox",
        category: "layouts",
        as: "div",
    },

    // Components
    {
        title: "Icon",
        category: "components",
        intro: (
            <>
                <Text tag="h2">How to use</Text>

                <Text>
                    Download SVG icons, and put them in the folder
                    <code>/public/icons</code>. Then you can use this component
                    normally. All the icons on this library come from{" "}
                    <a
                        href="https://boxicons.com/"
                        target="_blank"
                        rel="noreferrer noopener"
                    >
                        Boxicons
                    </a>
                    .
                </Text>
            </>
        ),
    },
    {
        title: "Key",
        category: "components",
        as: "span",
    },
    {
        title: "Text Icon",
        category: "components",
        as: "div",
        extend: "/styles/text",
    },
    {
        title: "Highlight",
        category: "components",
        as: "p",
        extend: "/styles/text",
    },
    {
        title: "Linkify",
        category: "components",
        as: "p",
        extend: "/styles/text",
        intro: "Detects if your text contains an url and creates a link.",
    },
    {
        title: "Hr",
        category: "components",
        as: "hr",
    },
    {
        title: "Skeleton",
        category: "components",
        intro: "Use this component to build a loading state.",
        include: ["Skeleton", "SkeletonShine", "SkeletonCard"],
    },
    {
        title: "Image",
        category: "components",
        as: "img",
    },
    {
        title: "Masonry",
        category: "components",
        as: "div",
    },
    {
        title: "Full Bleed",
        category: "components",
        intro: (
            <Text>
                For this component to work, always use the{" "}
                <Link to="/layouts/container">Main full</Link> layout.
            </Text>
        ),
        as: "div",
    },
    {
        title: "Video",
        category: "components",
        as: "video",
    },
    {
        title: "Youtube",
        category: "components",
        intro: "Here we convert automatically a YouTube url to an embed one.",
        as: "iframe",
    },
    {
        title: "Avatar",
        category: "components",
        as: "span",
    },
    {
        title: "Loader",
        category: "components",
        as: "span",
    },
    {
        title: "Burger",
        category: "components",
        as: "button",
    },
    {
        title: "Button",
        category: "components",
        as: "button",
    },
    {
        title: "Button Icon",
        category: "components",
        as: "button",
    },
    {
        title: "Button Group",
        category: "components",
        as: "div",
        types: ["ButtonGroupProps"],
    },
    {
        title: "Tag",
        category: "components",
        as: "span",
    },
    {
        title: "Truncate",
        category: "components",
        as: "div",
    },
    {
        title: "Card",
        category: "components",
        as: "div",
        intro: "Use this component as a base to build cards.",
        extend: "/layouts/flexbox",
    },
    {
        title: "Badge",
        category: "components",
        as: "span",
    },
    {
        title: "Tooltip",
        category: "components",
        as: "span",
    },
    {
        title: "Input",
        category: "components",
        types: ["ValidationTypes"],
    },
    {
        title: "Input Image",
        category: "components",
    },
    {
        title: "Input Check",
        category: "components",
    },
    {
        title: "Input Phone",
        category: "components",
        types: ["InputPhoneCountryType"],
    },
    {
        title: "Input Counter",
        category: "components",
    },
    {
        title: "Input Slider",
        category: "components",
    },
    {
        title: "Select",
        category: "components",
    },
    {
        title: "Autocomplete",
        category: "components",
        intro: (
            <Text>
                Here, we are using{" "}
                <a
                    href="https://fusejs.io/"
                    target="_blank"
                    rel="noreferrer noopener"
                >
                    Fuse.js
                </a>{" "}
                for fuzzy search.
            </Text>
        ),
    },
    {
        title: "Rating",
        category: "components",
    },
    {
        title: "Datepicker",
        category: "components",
    },
    {
        title: "Timepicker",
        category: "components",
        types: [
            "TimepickerHoursTypes",
            "TimepickerAllTimesTypes",
            "TimepickerHalfHoursTypes",
            "TimepickerQuarterHoursTypes",
        ],
    },
    {
        title: "Form",
        category: "components",
    },
    {
        title: "List Group",
        category: "components",
        as: "ul",
        types: ["ListGroupItemProps"],
    },
    {
        title: "Breadcrumbs",
        category: "components",
        as: "p",
        types: ["BreadcrumbsItemProps"],
    },
    {
        title: "Accordion",
        category: "components",
        as: "div",
        types: ["AccordionItemProps"],
    },
    {
        title: "Dropdown",
        category: "components",
        include: ["DropdownContainer", "Dropdown"],
        types: ["DropdownItemProps"],
    },
    {
        title: "Toast",
        category: "components",
        as: "div",
    },
    {
        title: "Alert",
        category: "components",
        as: (
            <>
                If the content is a string: <code>div</code>, else:{" "}
                <code>p</code>.
            </>
        ),
    },
    {
        title: "Modal",
        category: "components",
        as: "div",
    },
    {
        title: "Pagination",
        category: "components",
        include: [
            "Pagination",
            "usePaginatedData",
            "PaginationContainer",
            "PaginationButton",
        ],
    },
    {
        title: "Paginator",
        category: "components",
        as: "div",
        include: ["Paginator", "usePaginatedData"],
    },
    {
        title: "Stepper",
        category: "components",
        as: "div",
        types: ["StepProps"],
    },
    {
        title: "Progress Bar",
        category: "components",
        as: "meter",
    },
    {
        title: "Progress Circle",
        category: "components",
    },
    {
        title: "Tabs",
        category: "components",
        types: ["TabsItemProps"],
        as: "div",
    },
    {
        title: "Slideshow",
        category: "components",
        as: "div",
    },
    {
        title: "Cover",
        category: "components",
        as: "div",
    },
    {
        title: "Table",
        category: "components",
        as: "table",
    },
    {
        title: "Page Loading",
        category: "components",
        as: "div",
    },
    {
        title: "Sticky",
        category: "components",
        as: "div",
    },
    {
        title: "Messaging",
        category: "components",
        types: ["MessageProps"],
    },
    {
        title: "Markdown Editor",
        category: "components",
        intro: (
            <Text>
                Here, we are using{" "}
                <a
                    href="https://www.npmjs.com/package/markdown-to-jsx"
                    target="_blank"
                    rel="noreferrer noopener"
                >
                    markdown-to-jsx
                </a>{" "}
                to convert the value of the input to <code>JSX</code>.
            </Text>
        ),
    },
    {
        title: "Markdown Container",
        intro: (
            <Text>
                Here, we are using{" "}
                <a
                    href="https://www.npmjs.com/package/markdown-to-jsx"
                    target="_blank"
                    rel="noreferrer noopener"
                >
                    markdown-to-jsx
                </a>
                .
            </Text>
        ),
        category: "components",
    },
    {
        title: "Code Container",
        category: "components",
        intro: (
            <Text>
                Here, we are using{" "}
                <a
                    href="https://github.com/react-syntax-highlighter/react-syntax-highlighter"
                    target="_blank"
                    rel="noreferrer noopener"
                >
                    react-syntax-highlighter
                </a>
                .
            </Text>
        ),
    },
    {
        title: "Fade",
        category: "components",
        as: "div",
    },
    {
        title: "Back To Top",
        category: "components",
        as: "button",
    },
    {
        title: "Header",
        category: "components",
        as: "header",
    },
    {
        title: "Icon Menu",
        category: "components",
        as: "div",
        types: ["IconMenuItemProps"],
    },
    {
        title: "Footer",
        category: "components",
        as: "footer",
        types: ["FooterItemProps"],
    },
    {
        title: "Helmet",
        category: "components",
        intro: (
            <Text>
                A component for{" "}
                <a
                    href="https://www.npmjs.com/package/react-helmet"
                    target="_blank"
                    rel="noreferrer noopener"
                >
                    react-helmet
                </a>
                .
            </Text>
        ),
    },

    // Helpers
    {
        title: "Helpers",
        category: "helpers",
        include: false,
        intro: (
            <>
                <Text>
                    Helper class names. Use them like any other classes:
                </Text>

                <CodeContainer language="javascript">
                    {'<div className="center"></div>'}
                </CodeContainer>
            </>
        ),
    },
    {
        title: "Sr Only",
        category: "helpers",
        intro: "Hide elements on every devices except screen readers. You can also use it as a CSS helper class.",
        as: "div",
    },
    {
        title: "Variables",
        include: [
            "ThemeLight",
            "ThemeDark",
            "Overlays",
            "FontFamilies",
            "FontSizes",
            "FontWeights",
            "LineHeights",
            "Shadows",
            "Radiuses",
            "Layouts",
            "Spacers",
            "Transitions",
            "Breakpoints",
        ],
        category: "helpers",
    },
    {
        title: "Mixins",
        category: "helpers",
        intro: "Mixins for Styled Components.",
    },
    {
        title: "Js Utils",
        category: "helpers",
        intro: (
            <Text>
                Small JS functions, to help you during the development process.
                Here, we are just importing functions from{" "}
                <a
                    href="https://www.npmjs.com/package/ts-utils-julseb"
                    target="_blank"
                    rel="noreferrer noopener"
                >
                    this package
                </a>
                .
            </Text>
        ),
        include: [
            "calculateAverage",
            "calculateTotalSum",
            "camelToCapitalize",
            "capitalize",
            "convertDate",
            "convertDateShort",
            "convertPrice",
            "convertToEmail",
            "convertToPascal",
            "convertYoutube",
            "deleteDuplicates",
            "detectLanguage",
            "formatHour",
            "generateNumbers",
            "getFirstName",
            "getLastName",
            "getPercentage",
            "getRandom",
            "getRandomAvatar",
            "getRandomDate",
            "getRandomNumber",
            "getRandomString",
            "getRandomTime",
            "getTimeNow",
            "getToday",
            "getTomorrow",
            "emailRegex",
            "passwordRegex",
            "scrollToTop",
            "slugify",
            "stringifyPx",
            "unslugify",
            "uuid",
            "enableScroll",
            "disableScroll",
            "sortByFrequency",
        ].sort(),
    },
    {
        title: "Options Markdown",
        category: "helpers",
        include: false,
        intro: (
            <Text>
                Options for{" "}
                <Link to="/components/markdown-container">
                    MarkdownContainer
                </Link>{" "}
                component.
            </Text>
        ),
    },
    {
        title: "Component Props",
        category: "helpers",
        include: false,
        types: [
            "AccordionItemProps",
            "BreadcrumbsItemProps",
            "MessageProps",
            "TabsItemProps",
            "StepProps",
            "FooterItemProps",
            "ButtonGroupProps",
            "ListGroupItemProps",
            "IconMenuItemProps",
        ],
        intro: "If you want to use TypeScript in your project, you can use these types.",
    },
    {
        title: "Hooks",
        category: "helpers",
        intro: <Text>Some useful React hooks.</Text>,
        include: [
            "useClickOutside",
            "useCopyToClipboard",
            "useFetch",
            "useForm",
            "useKeyPress",
            "useMaxWidth",
            "useMinWidth",
            "useTranslation",
            "useTouchScreen",
            "usePaginatedData",
            "useExportData",
            "useDebounce",
        ],
    },
]
